import React, { useEffect, useState, useCallback, useRef } from "react";
import clsx from "clsx";
import API from "../../utils/axios";

import {
  Box,
  Checkbox,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  Typography,
  Tooltip,
  Link,
  Radio,
  ListItemIcon,
  ListItemText
} from "@material-ui/core";
import { connect } from "react-redux";
import { postOptimize, setProjectOptions } from "../../store/actions/project";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import { setLoadingProject } from "../../store/actions/loading";
import UnsavedChangesDialog from "../dialogs/UnsavedChangesDialog";
import {
  remCalc,
  handleKeypress,
  isPositiveInteger
} from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  setSettingsSaveDisabled,
  setOptionsExpanded
} from "../../store/actions/common";
import Form from "../common/Form";
import VehicleAutocomplete from "./VehicleAutocomplete";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { LANDING_URL } from "../data/constants";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import OptimizedTooltip from "../common/OptimizedTooltip";

const useStyles = makeStyles(theme => ({
  root: {
    width: remCalc(448),
    height: "100%",
    [theme.breakpoints.down("md")]: {
      width: "29rem"
    }
  },
  header: {
    height: 80,
    maxHeight: 80,
    borderBottom: "1px solid #EAEBF0",
    padding: theme.spacing(0, 2)
  },
  headerBackIconWrapper: {
    marginRight: theme.spacing(2)
  },
  headerTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: theme.typography.fontWeightMedium
  },
  optionGridItem: {
    padding: `${theme.spacing(0, 2, 0, 1)} !important`,
    height: remCalc(28),
    alignItems: "center"
  },
  switch: {
    marginLeft: theme.spacing(-1.25),
    marginRight: theme.spacing(0.5),
    "&.Mui-disabled": {
      opacity: 0.38
    }
  },
  cancelBtn: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    marginRight: theme.spacing(1),
    color: "#606165"
  },
  button: {
    minWidth: remCalc(46),
    textTransform: "unset",
    backgroundColor: theme.palette.primary.main,
    "&.Mui-disabled": {
      color: "#fff",
      opacity: 0.38,
      backgroundColor: theme.palette.primary.main
    }
  },
  mt0: {
    marginTop: theme.spacing(0)
  },
  mt12px: {
    marginTop: theme.spacing(1.5)
  },
  mb2: {
    marginBottom: theme.spacing(2)
  },
  infoWrapper: {
    paddingLeft: theme.spacing(5.5),
    marginBottom: theme.spacing(1.5),
    paddingRight: theme.spacing(1)
  },

  backhaulInfo: {
    whiteSpace: "normal",
    marginBottom: "12px"
  },
  switchFont: {
    fontSize: "0.9375rem",
    display: "flex",
    alignItems: "center"
  },
  fontWeightMedium: {
    fontWeight: 500
  },
  tooltip: {
    backgroundColor: "#fff",
    color: theme.palette.text.primary,
    fontSize: "0.9375rem",
    fontWeight: 400,
    boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.24)",
    padding: theme.spacing(1.25)
    // maxWidth: "600px"
  },
  betaFlag: {
    backgroundColor: "#D7F0E5",
    color: "#1A5B38",
    padding: theme.spacing(0.25, 0.5),
    lineHeight: ".75rem",
    fontSize: ".625rem",
    letterSpacing: "0.06em",
    display: "inline-block",
    fontWeight: "bold",
    marginLeft: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    cursor: "pointer"
  },
  select: {
    "&:focus": {
      background: "rgba(0,0,0,0)"
    },
    "& .MuiListItemText-root": {
      height: "auto",
      margin: 0
    },
    "& .MuiListItemText-secondary, .MuiListItemIcon-root": {
      display: "none"
    }
  },
  selectPaper: {
    maxWidth: 416
  },
  selectOption: {
    minHeight: 72,
    whiteSpace: "normal"
  },
  listItemText: {
    fontSize: "15px",
    lineHeight: "18px"
  },
  listItemIcon: {
    marginLeft: theme.spacing(-0.25),
    minWidth: 0,
    "& .MuiRadio-root": {
      padding: theme.spacing(0.25)
    },
    marginRight: theme.spacing(1.25),
    "& .Mui-checked svg": {
      color: theme.palette.primary.main
    }
  },
  listItemTextSecondary: {
    color: "#7D7E82",
    marginTop: theme.spacing(0.25)
  },
  withSubtitle: {
    height: theme.spacing(4.75),
    display: "flex",
    justifyContent: "center",
    flexDirection: "column"
  },
  infoIcon: {
    width: 18,
    height: 18,
    marginLeft: 8
  },
  darkTooltip: {
    padding: theme.spacing(1.25, 1.5),
    fontSize: "0.8125rem",
    lineHeight: "1.125rem",
    fontWeight: "normal",
    maxWidth: 248,
    boxSizing: "border-box",
    backgroundColor: "#606165",
    boxShadow: "2px 0px 12px rgba(0, 0, 0, 0.24)"
  },
  actionsWrapper: {
    height: 64,
    maxHeight: 64,
    boxSizing: "border-box",
    borderTop: "1px solid #EAEBF0",
    padding: theme.spacing(2, 3, 1, 3)
  }
}));

const maxLoad = 1000000;
const maxDuration = 1440;

function CreateOption({ title, subtitle, checked }) {
  const classes = useStyles();

  return (
    <>
      <ListItemIcon className={classes.listItemIcon}>
        <Radio color="primary" checked={checked} />
      </ListItemIcon>
      <ListItemText
        classes={{
          primary: classes.listItemText,
          secondary: clsx(classes.listItemText, classes.listItemTextSecondary)
        }}
        className={classes.withSubtitle}
        primary={title}
        secondary={subtitle}
      />
    </>
  );
}

const generateInitialValues = options => ({
  distance_units: options.distance_units,
  algorithm: "time",
  default_duration: options.default_duration,
  default_load: options.default_load,
  allow_late_stops: !!options.allow_late_stops_minute,
  allow_late_stops_minute: options.allow_late_stops_minute || "",
  allow_reload: options.allow_reload || false,
  fixed_departure_time: options.fixed_departure_time || false,
  reload_duration:
    options.reload_duration === null ? "5" : options.reload_duration,
  depot_address: options.depot_address
});

function Settings({
  expanded,
  setOptionsExpanded,
  options,
  postOptimize,
  setProjectOptions,
  setLoadingProject,
  solution,
  projectId,
  settingsSaveDisabled,
  setSettingsSaveDisabled,
  isPublished
}) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [changesDialog, setChangesDialog] = useState(false);

  const [inputValues, setInputValues] = useState(
    generateInitialValues(options)
  );

  const [errors, setErrors] = useState({
    default_duration: "",
    default_load: "",
    allow_late_stops_minute: ""
  });

  // Stays false if only distance unit changes
  const needReoptimize = useRef(false);

  useEffect(() => {
    setSettingsSaveDisabled(true);
    needReoptimize.current = false;
  }, [expanded, setSettingsSaveDisabled]);

  const handleChange = e => {
    const { name, value } = e.target;
    if (name !== "distance_units") needReoptimize.current = true;
    setInputValues({
      ...inputValues,
      [name]: value,
      ...(name === "allow_late_stops" && value === false
        ? { allow_late_stops_minute: null }
        : {})
      /*
      ...(name === "allow_reload" && value === true
        ? { fixed_departure_time: true }
        : {}),*/
    });

    if (value !== inputValues[name]) {
      setSettingsSaveDisabled(false);
    }
  };

  // Check for max value
  const handleNumInputChange = (e, maxValue) => {
    const { name, value } = e.target;
    setInputValues({
      ...inputValues,
      [name]: maxValue && value > Number(maxValue) ? maxValue : value
    });

    setSettingsSaveDisabled(false);
  };

  const { enqueueSnackbar } = useSnackbar();

  const saveChanges = needsToReoptimize => {
    setDialogOpen(false);
    setProjectOptions(inputValues);
    setSettingsSaveDisabled(true);

    const data = {
      algorithm: inputValues.algorithm || null,
      default_duration: inputValues.default_duration || null,
      default_load: inputValues.default_load || null,
      distance_units: inputValues.distance_units || null,
      allow_late_stops_minute: inputValues.allow_late_stops
        ? inputValues.allow_late_stops_minute
        : "",
      allow_reload: inputValues.allow_reload || false,
      fixed_departure_time: inputValues.fixed_departure_time || false,
      reload_duration: `${inputValues.reload_duration}` || "0",
      depot_address: inputValues.depot_address || null
    };

    API.patch(`v1/projects/${projectId}/configurations`, data)
      .then(res => {
        console.log(res);
        enqueueSnackbar("Routing settings updated.");
        // Reoptimize
        if (solution && needsToReoptimize) {
          setLoadingProject(true);

          postOptimize("Change project settings");
        }
      })
      .catch(error => {
        enqueueSnackbar(
          "Something went wrong, couldn't update configurations."
        );

        console.log(error);
      })
      .finally(() => {
        setSettingsSaveDisabled(true);
      });
  };

  // Validate inputs and submit
  const handleSave = () => {
    const errors = {
      default_duration: "",
      default_load: "",
      allow_late_stops_minute: "",
      depot_address: "",
      reload_duration: ""
    };

    if (!isPositiveInteger(inputValues.default_duration))
      errors.default_duration = "Duration must be a natural number";

    if (!isPositiveInteger(inputValues.default_load))
      errors.default_load = "Load must be a natural number";

    if (inputValues.allow_late_stops) {
      if (!inputValues.allow_late_stops_minute) {
        errors.allow_late_stops_minute =
          "Late stops duration should be entered.";
      } else if (!isPositiveInteger(inputValues.allow_late_stops_minute)) {
        errors.allow_late_stops_minute =
          "Late stops duration must be a positive number";
      }
    }

    if (inputValues.allow_reload) {
      if (!inputValues.depot_address) {
        errors.depot_address = "Depot address is required";
      }
      if (parseInt(inputValues.reload_duration, 10) < 0)
        errors.reload_duration =
          "Load duration per stop must be a natural number";
    }

    setErrors(errors);

    if (Object.values(errors).every(err => !err)) {
      saveChanges(false);
    }
  };

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
    setInputValues(generateInitialValues(options));

    setErrors({
      default_duration: "",
      default_load: "",
      allow_late_stops_minute: "",
      depot_address: "",
      reload_duration: ""
    });

    setOptionsExpanded(false);
    setSettingsSaveDisabled(true);
  }, [options]);

  // Warn before closing
  const warnClose = settingsSaveDisabled
    ? handleCancel
    : () => setChangesDialog(true);

  useEffect(() => {
    console.log({
      distance_units: options.distance_units,
      algorithm: options.algorithm,
      default_duration: options.default_duration || "",
      default_load: options.default_load,
      allow_late_stops: !!options.allow_late_stops_minute,
      allow_late_stops_minute: options.allow_late_stops_minute || "",
      allow_reload: options.allow_reload || false,
      fixed_departure_time: options.fixed_departure_time || false,
      reload_duration:
        options.reload_duration === null ? "5" : `${options.reload_duration}`,
      depot_address: options.depot_address
    })
    setInputValues({
      distance_units: options.distance_units,
      algorithm: options.algorithm,
      default_duration: options.default_duration || "",
      default_load: options.default_load,
      allow_late_stops: !!options.allow_late_stops_minute,
      allow_late_stops_minute: options.allow_late_stops_minute || "",
      allow_reload: options.allow_reload || false,
      fixed_departure_time: options.fixed_departure_time || false,
      reload_duration:
        options.reload_duration === null ? "5" : `${options.reload_duration}`,
      depot_address: options.depot_address
    });
  }, [expanded, options]);

  const handleAddressSelect = (field, value) => {
    setSettingsSaveDisabled(false);
    if (value && value.place_id) {
      // Get place details
      const service = new window.google.maps.places.PlacesService(
        window.document.createElement("div")
      );
      service.getDetails(
        {
          placeId: value.place_id,
          language: "en",
          fields: ["address_components", "geometry", "formatted_address"]
        },
        function (PlaceResult, PlacesServiceStatus) {
          console.log(value, PlaceResult);

          setInputValues({ ...inputValues, [field]: PlaceResult });
        }
      );
    } else {
      setInputValues({ ...inputValues, [field]: value });
    }
  };

  return (
    <Drawer open={expanded} variant="persistent">
      <Box className={classes.root}>
        <Grid container alignItems="center" className={classes.header}>
          <Grid item className={classes.headerBackIconWrapper}>
            <IconButton
              onClick={warnClose}
              data-id="route_settings_back_button"
            >
              <ArrowBackIcon />
            </IconButton>
          </Grid>
          <Grid item xs>
            <Typography className={classes.headerTitle}>
              Routing Settings
            </Typography>
          </Grid>
        </Grid>
        <Box
          pt={3}
          pb={1.5}
          px={3}
          style={{
            maxHeight: isPublished
              ? "calc(100vh - 80px)"
              : "calc(100vh - 144px)",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box"
          }}
        >
          {/* Form */}
          <Form
            data={[
              [
                {
                  labelId: "select-algorithm-label",
                  selectId: "select-algorithm",
                  label: "Optimization preference",
                  name: "algorithm",
                  dataCy: "algorithm",
                  i18nKey: true,
                  className: classes.select,
                  classes: {
                    paper: classes.selectPaper,
                    option: classes.selectOption
                  },
                  options: [
                    ["time", "Fastest time", "fastestTime"],
                    ["distance", "Shortest distance", "shortestDistance"]
                  ],
                  onChange: e => {
                    console.log(e.target.name, e.target.value);
                    handleChange(e);
                  },
                  componentType: "select",
                  disabled: isPublished
                }
              ],
              [
                {
                  labelId: "select-distance-units-label",
                  selectId: "select-distance-units",
                  label: "Distance Format",
                  name: "distance_units",
                  dataCy: "distance_units",
                  options: [
                    ["km", "KM"],
                    ["mi", "MI"]
                  ],
                  onChange: e => handleChange(e),
                  componentType: "select",
                  disabled: isPublished
                }
              ],
              [
                {
                  renderInputField: true,
                  label: "Default stop duration",
                  name: "default_duration",
                  dataCy: "default_duration",
                  onChange: e => {
                    handleNumInputChange(e, maxDuration);
                  },
                  onKeyDown: e => handleKeypress(e),
                  endAdornment: (
                    <InputAdornment position="end">minutes</InputAdornment>
                  ),
                  disabled: isPublished
                },
                {
                  renderInputField: true,
                  label: "Default stop load",
                  name: "default_load",
                  dataCy: "default_load",
                  onChange: e => {
                    handleNumInputChange(e, maxLoad);
                  },
                  onKeyDown: e => handleKeypress(e),
                  endAdornment: (
                    <InputAdornment position="end">units</InputAdornment>
                  ),
                  disabled: isPublished
                }
              ]
            ]}
            handleChange={handleChange}
            inputValues={inputValues}
            errorValues={errors}
            // settings
          />
          <Grid container spacing={2} className={classes.mt0}>
            <Box xs={12} component={Grid} className={clsx(classes.mt12px)}>
              <Box
                item
                className={classes.optionGridItem}
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
              >
                <Checkbox
                  name="allow_late_stops"
                  inputProps={{ "data-cy": "allow_late_stops" }}
                  onChange={e =>
                    handleChange({
                      target: {
                        name: "allow_late_stops",
                        value: e.target.checked
                      }
                    })
                  }
                  className={classes.switch}
                  checked={inputValues.allow_late_stops}
                  color="primary"
                  disabled={isPublished}
                />
                <Typography variant="body2" className={classes.switchFont}>
                  {t("allowLateStops")}{" "}
                  <Tooltip
                    interactive
                    title={
                      <>
                        When this is <b>turned on</b>, our routing engine is
                        allowed to schedule stops into a route even if this
                        means that a driver will not be able to make it on time.
                        <br />
                        <br />
                        For example, if a stop has a time-window of 10:00-12:00,
                        specifying that a driver is allowed to be 8 minutes late
                        at a stop, the routing engine will schedule that stop
                        into a route even if the arrival time is 12:08.
                      </>
                    }
                    placement="bottom-center"
                    classes={{ tooltip: classes.darkTooltip }}
                  >
                    <InfoOutlined className={classes.infoIcon} />
                  </Tooltip>
                </Typography>
              </Box>
              <div className={classes.infoWrapper}>
                <Typography
                  variant="body2"
                  color="secondary"
                  className={classes.backhaulInfo}
                >
                  Maximum amount of time drivers may be late at a stop
                </Typography>
                {inputValues.allow_late_stops && (
                  <Form
                    data={[
                      [
                        {
                          renderInputField: true,
                          label: t("allowLateStops"),
                          name: "allow_late_stops_minute",
                          dataCy: "allow_late_stops_minute",
                          onChange: e => {
                            handleNumInputChange(e, maxDuration);
                          },
                          onKeyDown: e => handleKeypress(e),
                          endAdornment: (
                            <InputAdornment position="end">
                              minutes
                            </InputAdornment>
                          ),
                          disabled: isPublished
                        }
                      ]
                    ]}
                    handleChange={handleChange}
                    inputValues={inputValues}
                    errorValues={errors}
                  />
                )}
              </div>
            </Box>
            <Box xs={12} component={Grid}>
              <Box
                item
                className={classes.optionGridItem}
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
              >
                <Checkbox
                  name="allow_reload"
                  inputProps={{ "data-cy": "allow_reload" }}
                  onChange={e => {
                    handleChange({
                      target: {
                        name: "allow_reload",
                        value: e.target.checked
                      }
                    });
                  }}
                  className={classes.switch}
                  checked={inputValues.allow_reload}
                  color="primary"
                  disabled={isPublished}
                />
                <Typography variant="body2" className={classes.switchFont}>
                  Allow reloads
                  <Tooltip
                    interactive
                    title={
                      <>
                        This feature is in beta testing, the way it works may
                        change at any time. User feedback is extremely valuable
                        to us, if you have any comments, please contact us{" "}
                        <Link
                          href={`${LANDING_URL}/en/contact/support`}
                          target="_blank"
                        >
                          here
                        </Link>{" "}
                        with details.
                      </>
                    }
                    placement="bottom-start"
                    classes={{ tooltip: classes.tooltip }}
                    // enterNextDelay={1000}
                    enterDelay={1000}
                  >
                    <span className={classes.betaFlag}>BETA</span>
                  </Tooltip>{" "}
                  <Tooltip
                    interactive
                    title={
                      <>
                        <b>Turn on</b> if you want to allow your vehicles to
                        return back to their start location in order to load
                        more stops. <br /> <br />
                        This could happen in cases when a vehicle does not have
                        the capacity to fulfill all the stops in one haul, but
                        has enough working hours left to come back to the depot
                        to load the remaining stops and fulfill them.
                      </>
                    }
                    placement="bottom-center"
                    classes={{ tooltip: classes.darkTooltip }}
                  >
                    <InfoOutlined className={classes.infoIcon} />
                  </Tooltip>
                </Typography>
              </Box>
              <div className={classes.infoWrapper}>
                <Typography
                  variant="body2"
                  color="secondary"
                  className={classes.backhaulInfo}
                >
                  {inputValues.allow_reload
                    ? `Vehicles may return to the depot to load more stops. All
                  vehicles will inherit the depot address and must depart at the
                  beginning of their working hours.`
                    : "Vehicles may return to the depot to load more stops."}
                </Typography>
                {inputValues.allow_reload && (
                  <>
                    <VehicleAutocomplete
                      inputId="backhaul-depot-location"
                      dataCy="backhaul-depot"
                      label="Depot address"
                      placeholder="Enter address"
                      initialValue={
                        inputValues.depot_address
                          ? inputValues.depot_address.formatted_address
                          : ""
                      }
                      shrinkLabel
                      isBackhaul
                      width={383}
                      handleSelect={selectedItem =>
                        handleAddressSelect("depot_address", selectedItem)
                      }
                      helperText={errors.depot_address || "*Required"}
                      error={!!errors.depot_address}
                      disabled={isPublished}
                    />
                    <Form
                      data={[
                        [
                          {
                            renderInputField: true,
                            label: "Loading time",
                            name: "reload_duration",
                            dataCy: "reload_duration",
                            onChange: e => {
                              handleNumInputChange(e, maxDuration);
                            },
                            onKeyDown: e => handleKeypress(e),
                            endAdornment: (
                              <InputAdornment position="end">
                                minutes
                              </InputAdornment>
                            ),
                            disabled: isPublished
                          }
                        ]
                      ]}
                      handleChange={handleChange}
                      inputValues={inputValues}
                      errorValues={errors}
                    />
                  </>
                )}
              </div>
            </Box>
            <Box
              component={Grid}
              className={classes.optionGridItem}
              item
              display="flex"
              justifyContent="flex-start"
              xs={12}
            >
              <OptimizedTooltip
                placement="bottom"
                disableHoverListener
                title={
                  inputValues.allow_reload ? (
                    <>
                      When <b>auto reloads</b> is on, vehicles must depart at
                      the start of their working hours.
                    </>
                  ) : (
                    ""
                  )
                }
                classes={{ tooltip: classes.darkTooltip }}
              >
                <span>
                  <Checkbox
                    name="fixed_departure_time"
                    inputProps={{ "data-cy": "fixed_departure_time" }}
                    onChange={e =>
                      handleChange({
                        target: {
                          name: "fixed_departure_time",
                          value: e.target.checked
                        }
                      })
                    }
                    className={classes.switch}
                    checked={inputValues.fixed_departure_time}
                    color="primary"
                    disabled={isPublished /*|| inputValues.allow_reload*/}
                  />
                </span>
              </OptimizedTooltip>
              <Typography variant="body2" className={classes.switchFont}>
                {t("fixedDeparture")}{" "}
                <Tooltip
                  interactive
                  title={
                    <>
                      When this is <b>turned off</b>, the departure time of a
                      vehicle may be delayed in order to reduce the working
                      hours.
                      <br /> <br />
                      When this is <b>turned on</b>, the vehicle will always
                      depart at the start of the defined working hours.
                    </>
                  }
                  placement="bottom-center"
                  classes={{ tooltip: classes.darkTooltip }}
                >
                  <InfoOutlined className={classes.infoIcon} />
                </Tooltip>
              </Typography>
            </Box>
            <div className={classes.infoWrapper}>
              <Typography
                variant="body2"
                color="secondary"
                className={classes.backhaulInfo}
              >
                Vehicles must depart at the start of their working hours
              </Typography>
            </div>
          </Grid>
        </Box>
        {!isPublished && (
          <Box
            display="flex"
            justifyContent={"flex-end"}
            className={classes.actionsWrapper}
          >
            <Button
              onClick={() => {
                if (!settingsSaveDisabled) {
                  setChangesDialog(true);
                } else {
                  handleCancel();
                }
              }}
              className={classes.cancelBtn}
              data-cy="discardSettings"
            >
              Cancel
            </Button>
            <Button
              className={classes.button}
              color="primary"
              variant="contained"
              data-cy="saveSettingsUpdate"
              onClick={() => {
                handleSave();
              }}
              disableElevation
              disabled={settingsSaveDisabled}
            >
              Save
            </Button>
          </Box>
        )}
      </Box>
      {/* Confirmation dialog */}
      <Dialog
        maxWidth="xs"
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Box fontWeight="fontWeightMedium" px={3} pt={3} pb={2}>
          Save changes to project settings?
        </Box>
        <Box pb={2} px={3}>
          <Typography variant="body2">
            By saving these changes, all the routes will be reoptimized based on
            the parameters and information you've entered.
          </Typography>
        </Box>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            color="primary"
            data-cy="discardSettings"
          >
            Cancel
          </Button>
          <Button
            onClick={() => saveChanges(true)}
            color="primary"
            autoFocus
            data-cy="confirmSettings"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Unsaved changes dialog */}
      <UnsavedChangesDialog
        open={changesDialog}
        handleClose={() => setChangesDialog(false)}
        action={handleCancel}
      />
    </Drawer>
  );
}

function mapStateToProps(state) {
  return {
    options: state.project.vrp.configuration,
    projectId: state.project.id,
    solution: state.project.solution,
    settingsSaveDisabled: state.common.settingsSaveDisabled,
    expanded: state.common.optionsExpanded,
    isPublished: ["published", "expired", "finished"].includes(
      state.project.status.toLowerCase()
    )
  };
}

export default connect(mapStateToProps, {
  setProjectOptions,
  setLoadingProject,
  setSettingsSaveDisabled,
  postOptimize,
  setOptionsExpanded
})(Settings);
